import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { pulseElement } from "../helpers/Animations";
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { getIngredients, searchDishes } from "../api/api_dishes";
import { addIngredientToDishThunk, getCountriesThunk, addProductComponentsThunk, addRecipeComponentsThunk } from "../store/slices/dishesReducer";
import { searchProducts, fetchProductByGtin } from "../api/api_products";
import { useTranslation } from "../hooks/useTranslation";
import {
  normalizeDecimalAmountInput,
  decimalAmountDisplayValue,
} from "../utils/decimalAmountInput";
import { purchasePricePerKgFromProduct } from "../utils/purchasePricePerKg";
import { normalizeGtin, sanitizeGtinInput } from "../utils/gtin";

// Remove the hardcoded COUNTRIES array since we'll get it from Redux store

const TableRowComponentNew = ({ itemId, adding, updateItem }) => {
  const dispatch = useDispatch();
  const { status, error, countries, currentDish } = useSelector((state) => state.dishes);
  const DEBUG_PRODUCT_PAYLOAD = false; // toggle to log product payload and skip API call
  const DEBUG_RECIPE_PAYLOAD = false; // toggle to log recipe payload and skip API call
  const { t, currentLanguage } = useTranslation();
  
  // Fetch countries when component mounts
  useEffect(() => {
    if (countries.length === 0) {
      dispatch(getCountriesThunk());
    }
  }, [dispatch, countries.length]);

  // Scroll to form when adding becomes true
  useEffect(() => {
    if (adding && formContainerRef.current) {
      setTimeout(() => {
        formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [adding]);

  // Clear error hide timeout on unmount
  useEffect(() => {
    return () => {
      if (hideErrorTimeoutRef.current) {
        clearTimeout(hideErrorTimeoutRef.current);
      }
    };
  }, []);

  // Transform countries to the format expected by react-select
  const countryOptions = countries.map(country => ({
    value: country.value,
    label: country.label
  }));

  console.log("Countries from store:", countries);
  console.log("Country options:", countryOptions);

  const [ingredients, setIngredients] = useState([{
    sourceType: 'ingredient', // ingredient | product | recipe
    selectedIngredient: null,
    selectedProduct: null,
    selectedRecipe: null,
    gtinInput: '',
    unit: '', // g, ml, kg, l
    portionCount: '',
    weightInAmount: 0,
    weightInPercentage: 0,
    isEco: false,
    selectedCountry: null,
    heading: '',
    comment: '',
    purchasePricePerKg: '',
    showMeta: false
  }]);

  // Default option lists for product/recipe when switching source
  const [productDefaultOptions, setProductDefaultOptions] = useState([]);
  const [recipeDefaultOptions, setRecipeDefaultOptions] = useState([]);
  
  const [showInputError, setShowInputError] = useState(false);
  const [inputErrorMessage, setInputErrorMessage] = useState("Något gick fel.");
  const inputError = useRef(null);
  const formContainerRef = useRef(null);
  const hideErrorTimeoutRef = useRef(null);

  const getLocalizedIngredientLabel = (ingredient) => {
    if (!ingredient) return '';
    const preferred = currentLanguage !== 'sv' ? ingredient.name_eng : ingredient.name;
    return preferred || ingredient.name || ingredient.name_eng || '';
  };

  const loadOptions = async (inputValue) => {
    try {
      const response = await getIngredients(inputValue);
      return response.data.map(ingredient => ({
        value: ingredient.id,
        label: getLocalizedIngredientLabel(ingredient),
        meta: ingredient // Include full ingredient data including density
      }));
    } catch (error) {
      console.error('Error loading ingredients:', error);
      return [];
    }
  };

  /* UI functions */
  const getInputTableStyle = () =>
    adding ? "w-full border-collapse border border-gray-300 bg-white rounded-lg shadow-sm" : "hidden";

  const displayInputError = (message) => {
    if (hideErrorTimeoutRef.current) {
      clearTimeout(hideErrorTimeoutRef.current);
      hideErrorTimeoutRef.current = null;
    }
    setInputErrorMessage(message);
    setShowInputError(true);
    setTimeout(() => {
      if (inputError.current) {
        pulseElement(inputError.current);
      }
    }, 0);
    hideErrorTimeoutRef.current = setTimeout(() => {
      setShowInputError(false);
      hideErrorTimeoutRef.current = null;
    }, 5000);
  };

  const resetInputValues = () => {
    setIngredients([{
      sourceType: 'ingredient',
      selectedIngredient: null,
      selectedProduct: null,
      selectedRecipe: null,
      gtinInput: '',
      unit: '',
      portionCount: '',
      weightInAmount: 0,
      weightInPercentage: 0,
      isEco: false,
      selectedCountry: null,
      heading: '',
      comment: '',
      purchasePricePerKg: '',
      showMeta: false
    }]);
    setShowInputError(false);
  };

  const addNewIngredientRow = () => {
    setIngredients(prev => ([...prev, {
      sourceType: 'ingredient',
      selectedIngredient: null,
      selectedProduct: null,
      selectedRecipe: null,
      gtinInput: '',
      unit: '',
      portionCount: '',
      weightInAmount: 0,
      weightInPercentage: 0,
      isEco: false,
      selectedCountry: null,
      heading: '',
      comment: '',
      purchasePricePerKg: '',
      showMeta: false
    }]));
  };

  const removeIngredientRow = (index) => {
    setIngredients(prev => {
      if (prev.length <= 1) return prev;
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const updateIngredient = (index, field, value) => {
    setIngredients(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Convert to grams for percentage calculation using ingredient density when available
  const convertToGrams = (unit, amount, ingredientDensity = null) => {
    const val = parseFloat(amount) || 0;
    if (!unit) return 0;
    
    // Helper function to convert volume to ml
    const convertToMl = (unit, amount) => {
      switch (unit) {
        case 'l': return amount * 1000;
        case 'dl': return amount * 100;
        case 'cl': return amount * 10;
        case 'ml': return amount;
        case 'pint': case 'pt': return amount * 473.176;
        case 'imppt': case 'imperial pint': return amount * 568.261;
        case 'quart': case 'qt': return amount * 946.353;
        case 'impqt': return amount * 1136.52;
        case 'gallon': case 'gal': return amount * 3785.41;
        case 'impgal': return amount * 4546.09;
        case 'floz': return amount * 29.5735;
        case 'impfloz': return amount * 28.4131;
        case 'cup': case 'uscup': return amount * 236.588;
        case 'metriccup': return amount * 250;
        case 'tbsp': case 'ustbsp': return amount * 14.7868;
        case 'tsp': case 'ustsp': return amount * 4.92892;
        case 'pinch': return amount * (4.92892 / 8);
        case 'dash': return amount * (4.92892 / 4);
        case 'drop': return amount * (4.92892 / 60);
        case 'msk': return amount * 15;
        case 'tsk': return amount * 5;
        case 'krm': return amount * 1;
        default: return 0;
      }
    };
    
    switch (unit) {
      // Mass units - convert to grams (base unit: kg in backend)
      case 'kg':
        return val * 1000; // 1 kg = 1000 g
      case 'g':
        return val; // 1 g = 1 g
      case 'mg':
        return val * 0.001; // 1 mg = 0.001 g
      case 'cg':
        return val * 0.01; // 1 cg = 0.01 g
      case 'µg':
        return val * 0.000001; // 1 µg = 0.000001 g
      case 'lb':
        return val * 453.592; // 1 lb = 453.592 g
      case 'oz':
        return val * 28.3495; // 1 oz = 28.3495 g
      
      // Volume units - convert to ml then use ingredient density or default to 1g/ml
      case 'l': case 'dl': case 'cl': case 'ml':
      case 'pint': case 'pt': case 'imppt': case 'imperial pint':
      case 'quart': case 'qt': case 'impqt':
      case 'gallon': case 'gal': case 'impgal':
      case 'floz': case 'impfloz':
      case 'cup': case 'uscup': case 'metriccup':
      case 'tbsp': case 'ustbsp': case 'tsp': case 'ustsp':
      case 'pinch': case 'dash': case 'drop':
      case 'msk': case 'tsk': case 'krm': {
        const ml = convertToMl(unit, val);
        const density = parseFloat(ingredientDensity) || 1.0; // Default to 1g/ml if no density
        
        // Debug logging to help troubleshoot density conversion
        if (ingredientDensity) {
          console.log(`Density conversion: ${val} ${unit} = ${ml} ml × ${density} g/ml = ${ml * density} g (using ingredient density)`);
        } else {
          console.log(`Density conversion: ${val} ${unit} = ${ml} ml × ${density} g/ml = ${ml * density} g (using default density - no ingredient density found)`);
        }
        
        return ml * density;
      }
      
      // Special units - keep as is (will need product-specific data)
      case 'styck':
        return val; // 1 styck = 1 g (needs product-specific data for accurate conversion)
      default:
        return val;
    }
  };


  const getUnitOptions = (ingredient) => {
    const hasDensity =
      ingredient?.sourceType !== 'ingredient' ||
      (ingredient?.selectedIngredient?.meta?.density != null &&
        Number(ingredient.selectedIngredient.meta.density) > 0);

    if (!hasDensity) {
      return [
        {
          label: 'Vikt (Mass)',
          options: [
            { value: 'kg', label: 'kg (kilogram)' },
            { value: 'g', label: 'g (gram)' }
          ]
        }
      ];
    }

    const options = [
      // Mass units (practical for restaurants)
      { label: 'Vikt (Mass)', options: [
        { value: 'kg', label: 'kg (kilogram)' },
        { value: 'g', label: 'g (gram)' },
        { value: 'mg', label: 'mg (milligram)' },
        { value: 'lb', label: 'lb (pound)' },
        { value: 'oz', label: 'oz (ounce)' }
      ]},
      
      // Volume units - Metric
      { label: 'Volym - Metrisk', options: [
        { value: 'l', label: 'l (liter)' },
        { value: 'dl', label: 'dl (deciliter)' },
        { value: 'cl', label: 'cl (centiliter)' },
        { value: 'ml', label: 'ml (milliliter)' }
      ]},
      
      // Volume units - US (practical for restaurants)
      { label: 'Volym - US', options: [
        { value: 'gallon', label: 'gallon (US)' },
        { value: 'quart', label: 'quart (US)' },
        { value: 'pint', label: 'pint (US)' },
        { value: 'floz', label: 'floz (US fluid ounce)' }
      ]},
      
      // Kitchen measurements
      { label: 'Köksmått', options: [
        { value: 'cup', label: 'cup (US cup)' },
        { value: 'tbsp', label: 'tbsp (tablespoon)' },
        { value: 'tsp', label: 'tsp (teaspoon)' },
        { value: 'msk', label: 'msk (matsked)' },
        { value: 'tsk', label: 'tsk (tesked)' },
        { value: 'krm', label: 'krm (kryddmått)' },
        { value: 'pinch', label: 'pinch (nypa)' },
        { value: 'dash', label: 'dash (skvätt)' }
      ]}
    ];

    // Only show "styck" option if ingredient has portion_size
    const hasPortionSize = ingredient?.sourceType === 'ingredient' && 
                          ingredient?.selectedIngredient?.meta?.portion_size && 
                          ingredient.selectedIngredient.meta.portion_size > 0;

    if (hasPortionSize) {
      options.push({
        label: 'Speciella enheter',
        options: [
          { value: 'styck', label: 'portion' }
        ]
      });
    }

    return options;
  };

  const recomputePercentages = (rows) => {
    // Sum existing dish grams (prefer components if available, else ingredients)
    const existingComponents = (currentDish?.components || []).reduce(
      (sum, comp) => sum + (parseFloat(comp.amount) || 0),
      0
    );
    const existingIngredients = (currentDish?.ingredients || []).reduce(
      (sum, ing) => sum + (parseFloat(ing.weight_in_amount) || 0),
      0
    );
    const existing = existingComponents > 0 ? existingComponents : existingIngredients;
    // Sum new grams
    const newGrams = rows.map(r => {
      const density = r.sourceType === 'ingredient' && r.selectedIngredient?.meta?.density 
        ? r.selectedIngredient.meta.density 
        : null;
      return convertToGrams(r.unit, r.weightInAmount, density);
    });
    const total = existing + newGrams.reduce((a,b)=>a+b,0);
    
    if (total <= 0) {
      return rows.map(r => ({ ...r, weightInPercentage: 0 }));
    }
    
    // Calculate percentages with proper rounding to avoid fractional issues
    const percentages = rows.map(r => {
      const density = r.sourceType === 'ingredient' && r.selectedIngredient?.meta?.density 
        ? r.selectedIngredient.meta.density 
        : null;
      const amount = convertToGrams(r.unit, r.weightInAmount, density);
      return (amount / total) * 100;
    });
    
    // Use largest remainder method to ensure percentages sum to 100%
    const roundedPercentages = largestRemainderMethod(percentages);
    
    const updated = rows.map((r, idx) => ({
      ...r,
      weightInPercentage: roundedPercentages[idx] || 0
    }));
    return updated;
  };

  // Largest remainder method to ensure percentages sum to exactly 100%
  const largestRemainderMethod = (percentages) => {
    const targetSum = 100;
    
    // Calculate floor values and remainders
    const floorValues = percentages.map(p => Math.floor(p));
    const remainders = percentages.map((p, i) => p - floorValues[i]);
    
    // Calculate how many units we need to distribute
    const currentSum = floorValues.reduce((sum, val) => sum + val, 0);
    const unitsToDistribute = targetSum - currentSum;
    
    // Sort by remainder (descending) to distribute units to largest remainders
    const sortedIndices = remainders
      .map((remainder, index) => ({ remainder, index }))
      .sort((a, b) => b.remainder - a.remainder)
      .map(item => item.index);
    
    // Distribute the remaining units
    const result = [...floorValues];
    for (let i = 0; i < unitsToDistribute; i++) {
      result[sortedIndices[i]]++;
    }
    
    return result;
  };

  useEffect(() => {
    // Only recompute percentages; do not reset sourceType
    setIngredients(prev => recomputePercentages(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDish]);

  const loadProductOptions = async (inputValue) => {
    try {
      const response = await searchProducts(inputValue, 10);
      let list = [];
      if (Array.isArray(response.data)) {
        list = response.data;
      } else if (response.data?.data) {
        list = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Handle object with numeric keys {"0": {...}, "1": {...}}
        list = Object.values(response.data);
      }
      const options = list.map(product => ({
        value: product.id,
        label: product.gtin ? `${product.name} • ${product.gtin}` : product.name,
        meta: product
      }));
      console.log('Product options loaded:', options.length);
      return options;
    } catch (e) {
      console.error('Error loading products:', e);
      return [];
    }
  };

  const loadRecipeOptions = async (inputValue) => {
    try {
      const response = await searchDishes(inputValue, undefined, currentDish?.id);
      const list = response.data?.data || response.data || [];
      return list.map(d => ({ value: d.id, label: d.name, meta: d }));
    } catch (e) {
      console.error('Error loading recipes:', e);
      return [];
    }
  }

  const loadDefaultProductOptions = async (index) => {
    try {
      // Default seed: vegan with limit 10
      let response = await searchProducts('vegan', 10);
      let list = [];
      if (Array.isArray(response.data)) list = response.data; else if (response.data?.data) list = response.data.data; else if (response.data && typeof response.data === 'object') list = Object.values(response.data);
      if (!list || list.length === 0) {
        // Fallback to generic list if no vegan results
        response = await searchProducts('', 10);
        if (Array.isArray(response.data)) list = response.data; else if (response.data?.data) list = response.data.data; else if (response.data && typeof response.data === 'object') list = Object.values(response.data);
      }
      const options = list.map(product => ({
        value: product.id,
        label: product.gtin ? `${product.name} • ${product.gtin}` : product.name,
        meta: product
      }));
      setProductDefaultOptions(options);
      // Don't auto-select or auto-fill when loading default options
      // Let user manually select from the dropdown
    } catch (e) {
      console.error('Failed loading default product options', e);
    }
  };

  const loadDefaultRecipeOptions = async () => {
    try {
      const response = await searchDishes('', undefined, currentDish?.id);
      const list = response.data?.data || response.data || [];
      const options = list.map(d => ({ value: d.id, label: d.name, meta: d }));
      setRecipeDefaultOptions(options);
    } catch (e) {
      console.error('Failed loading default recipe options', e);
    }
  };

  const validateIngredients = () => {
    for (let i = 0; i < ingredients.length; i++) {
      const ing = ingredients[i];
      if (ing.sourceType === 'ingredient' && !ing.selectedIngredient) {
        displayInputError(`${t('Välj en ingrediens för rad')} ${i + 1}.`);
        return false;
      }
      if (ing.sourceType === 'product' && !ing.selectedProduct) {
        displayInputError(`${t('Välj en produkt för rad')} ${i + 1}.`);
        return false;
      }
      if (ing.sourceType === 'recipe' && !ing.selectedRecipe) {
        displayInputError(`${t('Välj ett recept för rad')} ${i + 1}.`);
        return false;
      }
      if (!ing.unit) {
        displayInputError(`${t('Välj enhet för rad')} ${i + 1}.`);
        return false;
      }
      if (!ing.weightInAmount || parseFloat(ing.weightInAmount) <= 0) {
        displayInputError(`${t('Välj mängd för rad')} ${i + 1}.`);
        return false;
      }
      if (ing.sourceType === 'ingredient' && !ing.selectedCountry) {
        displayInputError(`${t('Välj ursprungsland för rad')} ${i + 1}.`);
        return false;
      }
      // Note: For 'product' and 'recipe', country is set automatically from metadata, so no validation needed
    }
    return true;
  };

  const handleAddIngredients = async () => {
    if (!validateIngredients()) {
      return;
    }

    try {
      const productRows = ingredients.filter(ing => ing.sourceType === 'product');
      const recipeRows = ingredients.filter(ing => ing.sourceType === 'recipe');

      // Recompute percentages with all rows considered
      const allRows = recomputePercentages(ingredients);
      const rowsByIndex = (type) => allRows
        .map((r, idx) => ({ row: r, idx }))
        .filter(x => x.row.sourceType === type);
      
      const ingredientsData = rowsByIndex('ingredient').map(({ row }) => {
        const density = row.selectedIngredient?.meta?.density || null;
        return {
          ingredient_id: row.selectedIngredient.value,
          weight_in_amount: convertToGrams(row.unit, row.weightInAmount, density),
          // Let backend calculate percentage from grams + existing dish total
          origin_country_id: row.selectedCountry?.value || null,
          is_eco: row.isEco || false,
          is_green: false,
          is_legume: false,
          heading: row.heading || null,
          comment: row.comment || null,
          chosen_unit: row.unit, // Store the chosen unit for evidence purposes
          inserted_value: row.weightInAmount, // Store the original value entered by user
          purchase_price_per_kg: row.purchasePricePerKg === '' ? null : Number(row.purchasePricePerKg)
        };
      });

      if (ingredientsData.length > 0) {
        await dispatch(addIngredientToDishThunk({ dishId: itemId, ingredients: ingredientsData })).unwrap();
      }

      if (productRows.length > 0) {
        const components = rowsByIndex('product').map(({ row }) => ({
          product_id: row.selectedProduct.value,
          weight_in_amount: convertToGrams(row.unit, row.weightInAmount),
          unit_id: null,
          origin_country_id: row.selectedCountry?.value || row.selectedProduct.meta?.origin_country_id || null,
          is_eco: row.isEco ?? !!(row.selectedProduct.meta?.is_eco || row.selectedProduct.meta?.is_organic),
          heading: row.heading || null,
          comment: row.comment || null,
          chosen_unit: row.unit, // Store the chosen unit for evidence purposes
          inserted_value: row.weightInAmount, // Store the original value entered by user
          purchase_price_per_kg: row.purchasePricePerKg === '' ? null : Number(row.purchasePricePerKg)
        }));
        if (DEBUG_PRODUCT_PAYLOAD) {
          // eslint-disable-next-line no-console
          console.log('DEBUG product payload → backend', { dishId: itemId, components });
          return; // skip API for debug
        }
        await dispatch(addProductComponentsThunk({ dishId: itemId, components })).unwrap();
      }

      if (recipeRows.length > 0) {
        const components = rowsByIndex('recipe').map(({ row }) => ({
          child_dish_id: row.selectedRecipe.value,
          weight_in_amount: convertToGrams(row.unit, row.weightInAmount),
          unit_id: null,
          origin_country_id: row.selectedCountry?.value || row.selectedRecipe.meta?.origin_country_id || null,
          heading: row.heading || null,
          comment: row.comment || null,
          chosen_unit: row.unit, // Store the chosen unit for evidence purposes
          inserted_value: row.weightInAmount, // Store the original value entered by user
          purchase_price_per_kg: null
        }));
        if (DEBUG_RECIPE_PAYLOAD) {
          // eslint-disable-next-line no-console
          console.log('DEBUG recipe payload → backend', { dishId: itemId, components });
          return; // skip API for debug
        }
        await dispatch(addRecipeComponentsThunk({ dishId: itemId, components })).unwrap();
      }

        updateItem();
        resetInputValues();
    } catch (error) {
      console.error("Error adding ingredients:", error);
      if (error.response?.data?.errors) {
        const errorMessage = Object.values(error.response.data.errors).flat().join(', ');
        displayInputError(errorMessage);
      } else if (error.message) {
        displayInputError(error.message);
      } else {
        displayInputError(`${t('Något gick fel vid tillägg av ingredienser.')}`);
      }
    }
  };

  if (status === "loading") {
    return <div className={getInputTableStyle()}>Laddar...</div>;
  }

  if (error) {
    return <div className={getInputTableStyle()}>Error: {error}</div>;
  }

  return (
    <div ref={formContainerRef} className="mt-6">
      <table className={getInputTableStyle()}>
        <thead className="bg-gray-50">
          <tr>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 w-32">{t('Källa')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 w-64">{t('Namn')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 w-[6.5rem]">{t('Enhet')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 w-40">{t('Mängd')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 w-40">{t('Inköpspris/kg')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 w-40 whitespace-nowrap">{t('Procent (auto)')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">{t('Ursprung/Produktion')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">{t('Alternativ')}</th>
            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">{t('Åtgärd')}</th>
          </tr>
        </thead>
        <tbody>
          {ingredients.map((ingredient, index) => (
            <React.Fragment key={index}>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="border border-gray-300 px-4 py-3 w-32">
                <Select
                  className="react-select-container w-32"
                  classNamePrefix="react-select"
                  value={{
                    value: ingredient.sourceType,
                    label: ingredient.sourceType === 'ingredient' ? 'Ingrediens' : ingredient.sourceType === 'product' ? 'Produkt' : 'Recept/Rätt'
                  }}
                  onChange={(opt) => {
                    const type = opt?.value || 'ingredient';
                    
                    // Reset all values when source type changes
                    updateIngredient(index, 'sourceType', type);
                    updateIngredient(index, 'selectedIngredient', null);
                    updateIngredient(index, 'selectedProduct', null);
                    updateIngredient(index, 'selectedRecipe', null);
                    updateIngredient(index, 'unit', '');
                    updateIngredient(index, 'portionCount', '');
                    updateIngredient(index, 'weightInAmount', 0);
                    updateIngredient(index, 'weightInPercentage', 0);
                    updateIngredient(index, 'selectedCountry', null);
                    updateIngredient(index, 'isEco', false);
                    updateIngredient(index, 'heading', '');
                    updateIngredient(index, 'comment', '');
                    updateIngredient(index, 'purchasePricePerKg', '');
                    
                    // Load default options for the new type
                    if (type === 'product') {
                      loadDefaultProductOptions(index);
                    } else if (type === 'recipe') {
                      loadDefaultRecipeOptions();
                    }
                    
                    // Recompute percentages after reset
                    setIngredients(prev => recomputePercentages(prev));
                  }}
                  options={[
                    { value: 'ingredient', label: 'Ingrediens' },
                    { value: 'product', label: 'Produkt' },
                    { value: 'recipe', label: 'Recept/Rätt' }
                  ]}
                />
              </td>

              <td className="border border-gray-300 px-4 py-3 w-64">
                <AsyncSelect
                  key={`dish-component-select-${index}-${ingredient.sourceType}-${currentLanguage}`}
                  cacheOptions
                  defaultOptions={ingredient.sourceType === 'product' ? productDefaultOptions : ingredient.sourceType === 'recipe' ? recipeDefaultOptions : true}
                  value={
                    ingredient.sourceType === 'ingredient'
                      ? (ingredient.selectedIngredient
                          ? {
                              ...ingredient.selectedIngredient,
                              label: getLocalizedIngredientLabel(
                                ingredient.selectedIngredient.meta || ingredient.selectedIngredient
                              ),
                            }
                          : null)
                      : ingredient.sourceType === 'product'
                        ? ingredient.selectedProduct
                        : ingredient.selectedRecipe
                  }
                  onChange={(value) => {
                    if (ingredient.sourceType === 'ingredient') {
                      updateIngredient(index, 'selectedIngredient', value);
                      
                      // If "styck" is selected but new ingredient doesn't have portion_size, clear the unit
                      if (ingredient.unit === 'styck' && (!value?.meta?.portion_size || parseFloat(value.meta.portion_size) <= 0)) {
                        updateIngredient(index, 'unit', '');
                        updateIngredient(index, 'portionCount', '');
                        updateIngredient(index, 'weightInAmount', 0);
                      } else if (ingredient.unit === 'styck' && value?.meta?.portion_size) {
                        const portionSize = parseFloat(value.meta.portion_size);
                        if (!Number.isNaN(portionSize) && portionSize > 0) {
                          const count = parseFloat(ingredient.portionCount) > 0 ? parseFloat(ingredient.portionCount) : 1;
                          updateIngredient(index, 'portionCount', String(count));
                          updateIngredient(index, 'weightInAmount', String(count * portionSize));
                        }
                      }
                      
                      // Debug: Log ingredient data including density
                      if (value?.meta) {
                        console.log('Selected ingredient:', {
                          id: value.value,
                          name: value.label,
                          density: value.meta.density,
                          hasDensity: !!value.meta.density,
                          portionSize: value.meta.portion_size
                        });
                      }
                    } else if (ingredient.sourceType === 'product') {
                      updateIngredient(index, 'selectedProduct', value);
                      // Auto-fill from product meta only when user selects a product
                      if (value?.meta) {
                        if (value.meta.origin_country_id) {
                          updateIngredient(index, 'selectedCountry', { value: value.meta.origin_country_id, label: countryOptions.find(c=>c.value===value.meta.origin_country_id)?.label || '' });
                        }
                        updateIngredient(index, 'isEco', !!(value.meta.is_eco || value.meta.is_organic));
                        // Auto-fill amount only when product has a meaningful size (> 0); input stays editable otherwise
                        if (value.meta.size) {
                          const sizeVal = parseFloat(value.meta.size);
                          if (!Number.isNaN(sizeVal) && sizeVal > 0) {
                            updateIngredient(index, 'weightInAmount', sizeVal);
                          }
                        }
                        // Always set unit so amount input stays enabled (default 'g' if product has none)
                        updateIngredient(index, 'unit', value.meta.default_unit_abbr || 'g');
                        updateIngredient(index, 'purchasePricePerKg', purchasePricePerKgFromProduct(value.meta));
                        // Recompute percentages after auto-fill
                        setIngredients(prev => recomputePercentages(prev));
                      }
                    } else {
                      updateIngredient(index, 'selectedRecipe', value);
                      updateIngredient(index, 'purchasePricePerKg', value?.meta?.purchase_price_per_kg ?? '');
                      // Auto-fill for recipe only when user selects a recipe
                      if (value?.meta) {
                        if (value.meta.origin_country_id) {
                          updateIngredient(index, 'selectedCountry', { value: value.meta.origin_country_id, label: countryOptions.find(c=>c.value===value.meta.origin_country_id)?.label || '' });
                        }
                        // Set default unit to 'g' for recipes
                        updateIngredient(index, 'unit', 'g');
                        if (value.meta.size) {
                          const sizeVal = parseFloat(value.meta.size);
                          if (!Number.isNaN(sizeVal)) {
                            updateIngredient(index, 'weightInAmount', sizeVal);
                          }
                        }
                        setIngredients(prev => recomputePercentages(prev));
                      }
                    }
                  }}
                  loadOptions={ingredient.sourceType === 'ingredient' ? loadOptions : ingredient.sourceType === 'product' ? loadProductOptions : loadRecipeOptions}
                  placeholder={ingredient.sourceType === 'ingredient' ? 'Ingrediens...' : ingredient.sourceType === 'product' ? 'Produkt...' : 'Recept/Rätt...'}
                  noOptionsMessage={() => (ingredient.sourceType === 'product' ? 'Inga produkter hittades' : ingredient.sourceType === 'recipe' ? 'Inga recept hittades' : 'Börja skriva för att söka')}
                  loadingMessage={() => "Laddar..."}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {ingredient.sourceType === 'product' && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={14}
                      placeholder={t('GTIN')}
                      value={ingredient.gtinInput || ''}
                      onChange={(e) => updateIngredient(index, 'gtinInput', sanitizeGtinInput(e.target.value))}
                      className="w-full max-w-[14rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      className="inline-flex items-center justify-center px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                      onClick={async () => {
                        const gtinValue = normalizeGtin(ingredient.gtinInput);
                        if (!gtinValue) {
                          displayInputError(`${t('Ange GTIN för rad')} ${index + 1}.`);
                          return;
                        }
                        try {
                          const res = await fetchProductByGtin(gtinValue);
                          const p = res.data;
                          if (!p || !p.id) {
                            displayInputError(`${t('Produkten hittades inte för rad')} ${index + 1}.`);
                            return;
                          }
                          const option = { value: p.id, label: p.gtin ? `${p.name} • ${p.gtin}` : p.name, meta: p };
                          updateIngredient(index, 'selectedProduct', option);
                          if (p.origin_country_id) {
                            updateIngredient(index, 'selectedCountry', { value: p.origin_country_id, label: countryOptions.find(c=>c.value===p.origin_country_id)?.label || '' });
                          }
                          updateIngredient(index, 'isEco', !!(p.is_eco || p.is_organic));
                          const pSize = parseFloat(p.size);
                          if (p.size != null && !Number.isNaN(pSize) && pSize > 0) {
                            updateIngredient(index, 'weightInAmount', pSize);
                          }
                          updateIngredient(index, 'unit', p.default_unit_abbr || 'g');
                          updateIngredient(index, 'purchasePricePerKg', purchasePricePerKgFromProduct(p));
                          updateIngredient(index, 'gtinInput', '');
                          setIngredients(prev => recomputePercentages(prev));
                        } catch (e) {
                          // eslint-disable-next-line no-console
                          displayInputError(e.response?.data?.error || e.message || `${t('Kunde inte hämta produkt för rad')} ${index + 1}.`);
                        }
                      }}
                    >
                      {t('Hämta')}
                    </button>
                  </div>
                )}
              </td>

              <td className="border border-gray-300 px-4 py-3 w-[6.5rem]">
                <Select
                  value={
                    ingredient.unit
                      ? {
                          value: ingredient.unit,
                          label: ingredient.unit === 'styck' ? 'portion' : ingredient.unit
                        }
                      : null
                  }
                  onChange={(value) => {
                    const selectedUnit = value?.value || '';
                    updateIngredient(index, 'unit', selectedUnit);
                    
                    // If "styck" is selected and ingredient has portion_size, set amount = count * portion_size
                    if (selectedUnit === 'styck' && ingredient.sourceType === 'ingredient' && ingredient.selectedIngredient?.meta?.portion_size) {
                      const portionSize = parseFloat(ingredient.selectedIngredient.meta.portion_size);
                      if (!Number.isNaN(portionSize) && portionSize > 0) {
                        const count = parseFloat(ingredient.portionCount) > 0 ? parseFloat(ingredient.portionCount) : 1;
                        updateIngredient(index, 'portionCount', String(count));
                        updateIngredient(index, 'weightInAmount', String(count * portionSize));
                      }
                    } else if (selectedUnit !== 'styck') {
                      updateIngredient(index, 'portionCount', '');
                    }
                    
                    setIngredients(prev => recomputePercentages(prev));
                  }}
                  options={getUnitOptions(ingredient)}
                  placeholder={t('Välj enhet...')}
                  className="react-select-container w-[6.5rem]"
                  classNamePrefix="react-select"
                  isSearchable={true}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                  isDisabled={ingredient.sourceType === 'product' || ingredient.sourceType === 'recipe'}
                  formatGroupLabel={(data) => (
                    <div style={{ fontWeight: 'bold', color: '#374151', fontSize: '12px', textTransform: 'uppercase' }}>
                      {data.label}
                    </div>
                  )}
                />
              </td>

              <td className="border border-gray-300 px-4 py-3 w-40">
                <input
                  className="w-full max-w-[10rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={decimalAmountDisplayValue(ingredient.weightInAmount)}
                  onChange={(e) => {
                    setShowInputError(false);
                    const next = normalizeDecimalAmountInput(e.target.value);
                    if (next === null) return;
                    updateIngredient(index, 'weightInAmount', next);
                    if (ingredient.unit === 'styck' && ingredient.sourceType === 'ingredient' && ingredient.selectedIngredient?.meta?.portion_size) {
                      const portionSize = parseFloat(ingredient.selectedIngredient.meta.portion_size);
                      const amount = parseFloat(next);
                      if (!Number.isNaN(portionSize) && portionSize > 0 && !Number.isNaN(amount)) {
                        updateIngredient(index, 'portionCount', String(amount / portionSize));
                      }
                    }
                    setIngredients(prev => recomputePercentages(prev));
                  }}
                  placeholder={t('Mängd')}
                  disabled={ingredient.sourceType === 'ingredient' ? !ingredient.unit : false}
                />
                {ingredient.unit === 'styck' && ingredient.sourceType === 'ingredient' && ingredient.selectedIngredient?.meta?.portion_size ? (
                  <div className="mt-2 w-full max-w-[10rem]">
                    <div className="mb-1 text-[11px] font-medium text-gray-600">{t('Antal portioner')}</div>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.1"
                      value={ingredient.portionCount ?? ''}
                      onChange={(e) => {
                        const count = e.target.value;
                        updateIngredient(index, 'portionCount', count);
                        const portionSize = parseFloat(ingredient.selectedIngredient?.meta?.portion_size);
                        const n = parseFloat(count);
                        if (!Number.isNaN(portionSize) && portionSize > 0 && !Number.isNaN(n)) {
                          updateIngredient(index, 'weightInAmount', String(n * portionSize));
                        } else if (count === '') {
                          updateIngredient(index, 'weightInAmount', '');
                        }
                        setIngredients(prev => recomputePercentages(prev));
                      }}
                      placeholder="t.ex. 2"
                    />
                    <div className="mt-1 text-[11px] text-gray-500">
                      {`1 ${t('portion')} = ${ingredient.selectedIngredient?.meta?.portion_size} g`}
                    </div>
                  </div>
                ) : null}
              </td>

              <td className="border border-gray-300 px-4 py-3 w-40">
                <input
                  className="w-full max-w-[10rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={ingredient.purchasePricePerKg ?? ''}
                  onChange={(e) => updateIngredient(index, 'purchasePricePerKg', e.target.value)}
                  placeholder={t('kr/kg')}
                  disabled={ingredient.sourceType === 'recipe'}
                />
              </td>

              <td className="border border-gray-300 px-4 py-3 w-40">
                <input
                  className="w-full max-w-[10rem] px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  type="number"
                  value={ingredient.weightInPercentage || 0}
                  readOnly
                />
              </td>

              <td className="border border-gray-300 px-4 py-3">
                <Select
                  value={ingredient.selectedCountry}
                  onChange={(value) => updateIngredient(index, 'selectedCountry', value)}
                  options={countryOptions}
                  placeholder={t('Ursprungsland...')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={ingredient.sourceType === 'product' || ingredient.sourceType === 'recipe'}
                />
              </td>

              <td className="border border-gray-300 px-4 py-3">
                  <label className={`flex items-center gap-2 ${ingredient.sourceType === 'product' ? 'cursor-not-allowed opacity-90' : ''}`}>
                    <input
                      type="checkbox"
                      checked={ingredient.isEco}
                      onChange={(e) => updateIngredient(index, 'isEco', e.target.checked)}
                      disabled={ingredient.sourceType === 'product'}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:cursor-not-allowed"
                    />
                    <span className="text-sm text-gray-700">{t('Ekologisk')}</span>
                  </label>
              </td>

              <td className="border border-gray-300 px-4 py-3">
                <div className="flex justify-center gap-2 relative">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 hover:bg-gray-200 text-white transition-colors"
                    onClick={() => setIngredients(prev => {
                      const next = [...prev];
                      next[index] = { ...next[index], showMeta: !next[index].showMeta };
                      return next;
                    })}
                    title={ingredient.showMeta ? t('Dölj detaljer') : t('Redigera detaljer')}
                  >
                    <img 
                      src="/assets/icons/Redigera.png" 
                      alt="Edit" 
                      className="w-4 h-4"
                    />
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => removeIngredientRow(index)}
                    disabled={ingredients.length === 1}
                  >
                    <img 
                      src="/assets/icons/Radera.png" 
                      alt="Delete" 
                      className="w-4 h-4"
                    />
                  </button>
                </div>
              </td>
            </tr>
            {ingredient.showMeta && (
              <tr>
                <td colSpan="9" className="border border-gray-300 px-4 py-3 bg-gray-50">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('Rubrik')}</label>
                      <input
                        type="text"
                        value={ingredient.heading || ''}
                        onChange={(e) => updateIngredient(index, 'heading', e.target.value)}
                        placeholder={t('Grupp/rubrik (valfri)')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kommentar</label>
                      <input
                        type="text"
                        value={ingredient.comment || ''}
                        onChange={(e) => updateIngredient(index, 'comment', e.target.value)}
                        placeholder={t('Kommentar (valfritt)')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}
          <tr>
            <td colSpan="9" className="border border-gray-300 px-4 py-3 text-center">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                onClick={addNewIngredientRow}
              >
                <img
                  alt="Lägg till"
                  className="w-4 h-4"
                  src={`${process.env.PUBLIC_URL}/assets/icons/Plus.png`}
                />
                <span>{t('Lägg till rad')}</span>
              </button>
            </td>
          </tr>
          <tr>
            <td colSpan="9" className="border border-gray-300 px-4 py-3 text-center">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                onClick={handleAddIngredients}
                disabled={status === "loading"}
              >
                <img
                  alt="Spara"
                  className="w-4 h-4"
                  src={`${process.env.PUBLIC_URL}/assets/icons/Check.png`}
                />
                <span>{t('Spara alla ingredienser')}</span>
              </button>
            </td>
          </tr>
          {showInputError && (
            <tr>
              <td colSpan="8" className="text-center py-2 text-amber-800 bg-amber-50 border border-amber-200 font-medium" ref={inputError}>
                {inputErrorMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableRowComponentNew;